import React, { useState } from 'react';
import styled from 'styled-components';
import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 400px;
  color: ${props => props.theme.colors.text};
`;

const ModalTitle = styled.h2`
  margin: 0 0 30px 0;
  text-align: center;
  color: ${props => props.theme.colors.text};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.colors.inputBackground || props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#007bff'};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoginButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary || '#007bff'};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primaryDark || '#0056b3'};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.borderColor};
  color: ${props => props.theme.colors.text};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.borderColor};
    opacity: 0.8;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
`;

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await signIn({ username, password });

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequiresPasswordChange(true);
        return;
      }

      // Get the access token from Amplify and store it in localStorage
      const session = await fetchAuthSession();
      if (session?.tokens?.idToken) {
        const tokenString = session.tokens.idToken.toString();
        localStorage.setItem('access_token', tokenString);
        // Store user initial (first letter of username)
        localStorage.setItem('user_initial', username.charAt(0).toUpperCase());
      }

      setUsername('');
      setPassword('');
      onLoginSuccess();
      onClose();
      // Refresh the page to update all components with new auth state
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      await confirmSignIn({ challengeResponse: newPassword });
      
      // Get the access token from Amplify and store it in localStorage
      const session = await fetchAuthSession();
      if (session?.tokens?.idToken) {
        localStorage.setItem('access_token', session.tokens.idToken.toString());
        // Store user initial (first letter of username)
        localStorage.setItem('user_initial', username.charAt(0).toUpperCase());
      }
      
      setUsername('');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setRequiresPasswordChange(false);
      onLoginSuccess();
      onClose();
      // Refresh the page to update all components with new auth state
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to change password. Please try again.');
      console.error('Password change error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setUsername('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setRequiresPasswordChange(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {requiresPasswordChange ? (
          <>
            <ModalTitle>Change Password</ModalTitle>
            <p style={{ marginBottom: '20px', color: 'inherit' }}>
              Your password has expired. Please set a new password to continue.
            </p>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <form onSubmit={handlePasswordChange}>
              <FormGroup>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  autoFocus
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <LoginButton type="submit" disabled={loading}>
                  {loading ? 'Changing Password...' : 'Change Password'}
                </LoginButton>
                <CancelButton type="button" onClick={handleCancel} disabled={loading}>
                  Cancel
                </CancelButton>
              </ButtonGroup>
            </form>
          </>
        ) : (
          <>
            <ModalTitle>Login</ModalTitle>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  autoFocus
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <LoginButton type="submit" disabled={loading}>
                  {loading ? 'Logging in...' : 'Login'}
                </LoginButton>
                <CancelButton type="button" onClick={handleCancel} disabled={loading}>
                  Cancel
                </CancelButton>
              </ButtonGroup>
            </form>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default LoginModal;
